import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, Share, Plus, MoreVertical, Smartphone } from 'lucide-react';
import { usePWAInstall } from '@/hooks/usePWAInstall';

interface InstallAppModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InstallAppModal({ open, onOpenChange }: InstallAppModalProps) {
  const { canInstall, isInstalled, promptInstall, isIOS } = usePWAInstall();

  const handleInstall = async () => {
    const accepted = await promptInstall();
    if (accepted) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5" />
            Prenesi aplikacijo
          </DialogTitle>
          <DialogDescription>
            Namesti aplikacijo za hitrejšo izkušnjo in hiter dostop z domačega zaslona.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {isInstalled ? (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground">
                ✅ Aplikacija je že nameščena na vaši napravi.
              </p>
            </div>
          ) : canInstall ? (
            <Button onClick={handleInstall} className="w-full" size="lg">
              <Download className="w-4 h-4 mr-2" />
              Namesti aplikacijo
            </Button>
          ) : null}

          {!isInstalled && !canInstall && (
            <>
              {isIOS ? (
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm">iPhone / iPad (Safari)</h4>
                  <ol className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">1</span>
                      <span>Tapnite gumb <Share className="w-4 h-4 inline" /> <strong>Deli</strong> v Safariju</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">2</span>
                      <span>Pomaknite se navzdol in tapnite <Plus className="w-4 h-4 inline" /> <strong>Dodaj na začetni zaslon</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">3</span>
                      <span>Tapnite <strong>Dodaj</strong> za potrditev</span>
                    </li>
                  </ol>
                </div>
              ) : (
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm">Android (Chrome)</h4>
                  <ol className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">1</span>
                      <span>Tapnite gumb <MoreVertical className="w-4 h-4 inline" /> <strong>meni</strong> v Chromu</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">2</span>
                      <span>Tapnite <strong>Namesti aplikacijo</strong> ali <strong>Dodaj na začetni zaslon</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">3</span>
                      <span>Tapnite <strong>Namesti</strong> za potrditev</span>
                    </li>
                  </ol>
                </div>
              )}
            </>
          )}

          {!isInstalled && canInstall && (
            <div className="border-t pt-3 space-y-3">
              <p className="text-xs text-muted-foreground">
                Ali pa dodajte ročno prek menija brskalnika:
              </p>
              <div className="text-xs text-muted-foreground space-y-1">
                <p><strong>iOS Safari:</strong> Deli → Dodaj na začetni zaslon</p>
                <p><strong>Android Chrome:</strong> Meni → Namesti aplikacijo</p>
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground text-center border-t pt-3">
            To je bližnjica do spletne aplikacije — prenos iz trgovine ni potreben. Brez dodatnih dovoljenj.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
